/**
 * Vario embeds our frontend in an iframe with initial height 0. Without continuously sending
 * `postMessage({ appIdentifier, integrationId, height })` to the parent, the iframe stays
 * collapsed and the user sees a "blank" panel.
 *
 * Mirrors @vario-software/vario-app-framework-frontend/height.js + communication.js:
 *   - send `{ appIdentifier, integrationId, ...payload }` to window.parent
 *   - on every DOM mutation + ResizeObserver, post the new scrollHeight
 *   - installation complete / cancel handlers route through the same message channel
 */
import { buildAppManagementRouteTarget, buildAppRouteTarget } from './lib/vario-route.js';

function readUrlParam(name: string): string | undefined {
  return new URL(window.location.href).searchParams.get(name) ?? undefined;
}

export const appIdentifier = readUrlParam('appIdentifier');
export const integrationId = readUrlParam('integrationId');

export function sendVarioMessage(payload: Record<string, unknown>) {
  try {
    window.parent.postMessage({ appIdentifier, integrationId, ...payload }, '*');
  } catch {
    // ignore - top-level open
  }
}

/**
 * VARIO-Ready-Signal (App-Framework-Update, Host-Auswertung ab 2026-08-20):
 * Meldet dem Host `{ appIdentifier, integrationId, ready: true }` = App vollständig
 * geladen und kommunikationsbereit. Erst danach gilt die App als erreichbar
 * (Install/Uninstall werden nur dann ausgeführt), sonst Ladeanimation/Fehler.
 * Nach erfolgreichem Bootstrap EINMAL aufrufen.
 */
export function signalVarioReady() {
  sendVarioMessage({ ready: true });
}

let lastReported = -1;

/**
 * Iframe-Hoehe je nach Slot-Kontext:
 *   - "fixed": konstanter Korridor (900px) fuer Dashboard-Slot, wo lange
 *     Inhalte (Storefront-Grid, AG-Grid Listings) sonst Endlos-Scroll im
 *     Vario-Parent ausloesen wuerden. Die App-Shell scrollt intern.
 *   - "auto":  ResizeObserver auf body, postet die aktuelle scrollHeight (mit
 *     small min-cap), damit kurze Listing-Sidebar-Slots ihre tatsaechliche
 *     Hoehe bekommen. Vorgesehen fuer listing-status / listing-category.
 */
const FIXED_HEIGHT = 900;
// 540 entspricht der Hoehe der "Ready"-Box im listing-status-Slot (Aktuell-Sektion,
// Alert "Artikel ist zum Listen bereit" + Aktionsbuttons + Padding). Vorher 120 →
// iframe kollabierte auf einen schmalen Streifen, in dem die Buttons nicht mehr
// sichtbar waren. 540 lässt die Ready-Box voll sehen, ohne grosse Auto-States
// (Lookup/Bilder/Decide) zu blockieren - die duerfen bis AUTO_MAX wachsen.
const AUTO_MIN_HEIGHT = 540;
// AUTO_MAX_HEIGHT: praktisch unbegrenzt (grow-to-fit, wie Ottos otto-listing-status mit
// max=Infinity). Ein harter Deckel (vorher 720) liess das iframe ueberlaufen, OHNE dass
// Vario den iframe-eigenen Scrollbalken zulaesst (Vario clippt den Container) → Inhalt
// unten unerreichbar. Stattdessen melden wir die volle Content-Hoehe; das iframe waechst
// auf den Inhalt und die Vario-Article-Seite scrollt normal. Min-Cap haelt kompakte
// Ready-Box bedienbar.
const AUTO_MAX_HEIGHT = 100000;

function postHeight(height: number) {
  if (height === lastReported) return;
  lastReported = height;
  sendVarioMessage({ height });
}

/**
 * Vario das Höhen-Handling KOMPLETT übergeben: Statt unsere Content-Höhe zu melden (das alte
 * `initVarioIframeHeight`-Verfahren → Render-Springen), fordern wir einmalig die volle verfügbare
 * Viewport-Höhe an. Ab dieser Message setzt Vario das iframe auf die volle Höhe und rechnet bei
 * Layout-Änderungen (Sidebar auf/zu, Resize) selbst neu. `appIdentifier`+`integrationId` hängt
 * `sendVarioMessage` automatisch an (fehlen sie, ignoriert Vario die Message).
 *
 * App-Seite: Layout auf 100% Höhe auslegen und das Scrolling SELBST übernehmen (vgl. den
 * `/#/orders`-Slot: `.go-orders-fullpage` + 100%-Ancestor-Kette in orders-page.ts). NICHT mit
 * `initVarioIframeHeight` kombinieren - dessen Höhen-postMessages würden dagegen arbeiten.
 */
export function requestVarioFullHeight() {
  sendVarioMessage({ useFullHeight: true });
}

/**
 * Im VARIO-Host auf einen ANDEREN Tab derselben App wechseln (dokumentierte Framework-Nachricht
 * `openInSameTab`, vue-router-Location). Die Tab-Leiste über dem iframe gehört VARIO — eine
 * interne `window.location`-Navigation ändert nur unseren Inhalt, nicht die aktive Registerkarte.
 *
 * Gibt `false` zurück, wenn die Ziel-Route nicht gebaut werden kann (App läuft nicht im Host);
 * der Aufrufer navigiert dann selbst. Ein `true` heisst NUR „Nachricht abgeschickt" — ob der Host
 * sie ausführt, sieht man erst daran, dass er das iframe austauscht. Aufrufer sollten deshalb
 * einen Rückfall auf die interne Navigation einplanen.
 *
 * Nur für Ziele am SELBEN Integrationspunkt (Verkaufskanal-Tab → Verkaufskanal-Tab). Für einen
 * Tab in der Appverwaltung `openVarioAppManagementTab` nehmen.
 */
export function openVarioAppTab(integrationId: string): boolean {
  const target = buildAppRouteTarget(window.location.search, integrationId);
  if (!target) return false;
  sendVarioMessage({ openInSameTab: target });
  return true;
}

/**
 * Wie `openVarioAppTab`, aber für einen Reiter in der Appverwaltung (System → Apps → App), z. B.
 * `gambio-settings` seit 2026-09-14. Route und Parameter: `buildAppManagementRouteTarget`.
 */
export function openVarioAppManagementTab(integrationId: string): boolean {
  const target = buildAppManagementRouteTarget(window.location.search, integrationId);
  if (!target) return false;
  sendVarioMessage({ openInSameTab: target });
  return true;
}

export type IframeHeightMode = 'fixed' | 'auto';

export function initVarioIframeHeight(mode: IframeHeightMode = 'fixed') {
  if (mode === 'fixed') {
    postHeight(FIXED_HEIGHT);
    window.addEventListener('resize', () => postHeight(FIXED_HEIGHT));
    return;
  }

  // Auto-Mode: aktuelle Inhaltshoehe (Body-scrollHeight) tracken und posten.
  // Wir clampen auf [AUTO_MIN, AUTO_MAX], damit weder kollabierte Anfangs-
  // Renders (Hoehe 0) noch versehentlich endlos-wachsende Inhalte das Vario-
  // iframe sprengen. Updates sind throttled per requestAnimationFrame —
  // mehrere Mutations im selben Frame fuehren nur zu einem postMessage.
  let rafScheduled = false;
  const measureAndPost = () => {
    rafScheduled = false;
    const raw = document.body?.scrollHeight ?? AUTO_MIN_HEIGHT;
    const clamped = Math.max(AUTO_MIN_HEIGHT, Math.min(AUTO_MAX_HEIGHT, raw));
    postHeight(clamped);
  };
  const schedule = () => {
    if (rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(measureAndPost);
  };
  schedule();
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(schedule).observe(document.body);
  }
  // Backup-Listener fuer dyn. Content-Inserts ohne layout-Shift, die der
  // ResizeObserver allein nicht zuverlaessig sieht.
  const mo = new MutationObserver(schedule);
  mo.observe(document.body, { childList: true, subtree: true, attributes: true });
  window.addEventListener('resize', schedule);
  window.addEventListener('load', schedule);
}
